/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Input,
  DatePicker,
  Alert,
} from 'antd';
import { formatReceivedValues } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import percentFormatter from 'utils/percentFormatter';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import PercentageInput from 'components/PercentageInput';
import ModalDelete from 'components/ModalDelete';
import NumericInput from 'components/NumericInput';
import CurrencyInput from 'components/CurrencyInput';
import {
  FLOAT_NUMER_PATTERN,
  integerRegExp,
  SLUG_PATTERN,
} from 'utils/patterns';
import Select from 'components/Select';
import { getEstadosGlobales, getPeriodicidadesDePagos } from 'api/catalogos/index';
import {
  getPercepcionesConfiguracionesISN,
  postPercepcionConfiguracionISN,
  deletePercepcionConfiguracionISN,
  patchPercepcionConfiguracionISN,
  getPercepcionConfiguracionISN,
} from 'api/nominas/percepciones/percepcionesConfiguracionesISN';
import {
  getPercepciones,
} from 'api/nominas/percepciones/percepciones';
import {
  getAplicacionesContribuciones,
} from 'api/nominas/catalogos/aplicacionesContribuciones';
import { checkToken } from 'api/utils';
import { CONTRIBUCION_NO_APLICA, CONTRIBUCION_PARCIALMENTE, ESTATUS_AUTORIZADO } from 'utils/constants';
import TextArea from 'antd/lib/input/TextArea';

export const percepcionConfiguracionISNPermission = {
  permissionModel: 'percepcionconfiguracionisn',
  catalogsModels: [],
  tabsModels: [],
};

const baseURI = 'nominas/percepciones-configuraciones-isn';

const PercepcionConfiguracionISN = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [percepciones, setPercepciones] = useState([]);
  const [aplicaciones, setAplicaciones] = useState([]);
  const [aplicaPorcentaje, setAplicaPorcentaje] = useState(false);
  const [aplicaLimite, setAplicaLimite] = useState(false);
  const [periodicidades, setPeriodicidades] = useState([]);
  const [aplicaUMAExcento, setAplicaUMAExcento] = useState(false);
  const [aplicaImporte, setAplicaImporte] = useState(false);
  const [aplicacionISN, setAplicacionISN] = useState(false);
  const [aplicaFundamento, setAplicaFundamento] = useState(false);
  const [percepcionesFiltered, setPercepcionesFiltered] = useState([]);

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
        percepcion_concepto: e?.percepcion,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const formInstances = [form];

  const onChangeAplicacion = (val) => {
    if (val === CONTRIBUCION_PARCIALMENTE) {
      setAplicacionISN(false);
    } else {
      setAplicacionISN(true);
      form.setFieldsValue({
        aplica_no_umas_excento: false,
        no_umas_excento: 0,
        aplica_porcentaje_excento: false,
        porcentaje_excento: 0,
        aplica_limite_de_excencion: false,
        no_umas_limite_de_excencion: 0,
        periodicidad_de_pago: null,
      });
      setAplicaImporte(form.getFieldValue('aplica_importe_excento'));
      setAplicaPorcentaje(form.getFieldValue('aplica_porcentaje_excento'));
      setAplicaLimite(form.getFieldValue('aplica_limite_de_excencion'));
      setAplicaUMAExcento(form.getFieldValue('aplica_no_umas_excento'));
      setAplicaFundamento(val === CONTRIBUCION_NO_APLICA);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getPercepcionesConfiguracionesISN();
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _percepciones,
        _aplicaciones,
        _periodicidades,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getPercepciones({ estados_globales: ESTATUS_AUTORIZADO }),
        getAplicacionesContribuciones(),
        getPeriodicidadesDePagos({ estados_globales: ESTATUS_AUTORIZADO }),
        getPercepcionesConfiguracionesISN(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setPercepciones(_percepciones);
        setAplicaciones(_aplicaciones);
        setPeriodicidades(_periodicidades);
        setPercepcionesFiltered(
          _percepciones.filter((e) => !_data.some((val) => val.percepcion === e.id)),
        );
        setData(formatReceivedClone(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setPercepcionesFiltered(
      percepciones.filter((e) => !data.some((val) => val.percepcion === e.id)),
    );
    setVisible(false);
    form.resetFields();
    setAplicaPorcentaje(false);
    setAplicaImporte(false);
    setAplicaLimite(false);
    setAplicaUMAExcento(false);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _percepcion = await getPercepcionConfiguracionISN(match.id);
    setTimeout(() => {
      if (_percepcion) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceivedClone(_percepcion));
          setVisible(true);
          setAplicaImporte(form.getFieldValue('aplica_importe_excento'));
          setAplicaPorcentaje(form.getFieldValue('aplica_porcentaje_excento'));
          setAplicaLimite(form.getFieldValue('aplica_limite_de_excencion'));
          setAplicaUMAExcento(form.getFieldValue('aplica_no_umas_excento'));
          setPercepcionesFiltered(
            percepciones.filter((e) => !data.some((val) => {
              if (val.percepcion === _percepcion.percepcion) {
                return false;
              }
              return val.percepcion === e.id;
            })),
          );
        });
      }
    });
    setLoading(false);
    const campo_aplicacion = form.getFieldValue('aplicacion_contribucion');
    onChangeAplicacion(campo_aplicacion);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchPercepcionConfiguracionISN(key, values)
        : postPercepcionConfiguracionISN(values)
      );
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePercepcionConfiguracionISN(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const contentModalDelete = (val) => percepciones.find((e) => e.id === val)?.clave;

  const columns = [
    {
      titleText: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      titleText: 'Clave',
      dataIndex: 'percepcion',
      key: 'percepcion',
      width: 200,
      render: (val) => percepciones.find((e) => e.id === val)?.clave,
    },
    {
      titleText: 'Concepto de Percepción',
      dataIndex: 'percepcion_concepto',
      key: 'percepcion_concepto',
      width: 300,
      render: (val) => percepciones.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Fundamento ISN',
      dataIndex: 'fundamento',
      key: 'fundamento',
      width: 200,
    },
    {
      titleText: 'Aplicación ISN',
      dataIndex: 'aplicacion_contribucion',
      key: 'aplicacion_contribucion',
      width: 200,
      render: (val) => aplicaciones.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'No. UMAS Exentos',
      dataIndex: 'no_umas_excento',
      key: 'no_umas_excento',
      width: 200,
    },
    {
      titleText: '% de Exención',
      dataIndex: 'porcentaje_excento',
      key: 'porcentaje_excento',
      width: 200,
      align: 'right',
      render: (val) => percentFormatter(val || 0),
    },
    {
      titleText: 'UMAS Límite de Exención',
      dataIndex: 'no_umas_limite_de_excencion',
      key: 'no_umas_limite_de_excencion',
      width: 250,
    },
    {
      titleText: 'Periodicidad de Aplicación de la Exención',
      dataIndex: 'periodicidad_de_pago',
      key: 'periodicidad_de_pago',
      width: 300,
      render: (val) => periodicidades.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
    descripcion: [
      requiredRule,
      {
        pattern: SLUG_PATTERN,
        message: 'Ingrese una clave válida',
      },
    ],
    dias: [
      requiredRule,
      {
        pattern: integerRegExp,
        message: 'Ingrese un valor válida',
      },
    ],
    valor: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
    ],
    porcentaje: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
      {
        validator: async () => {
          const { porcentaje_excento } = form.getFieldValue();
          if (porcentaje_excento > 100) {
            throw new Error('Este campo no puede ser mayor a 100%');
          }
        },
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Table
            cols={columns}
            data={data}
            titleTable="ISN Percepciones"
            rowSelection={rowSelection}
            allowExportPDF={false}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            baseURI={baseURI}
            allowImport
            permission={permission}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Percepción Configuración para ISN`}
            extra={(
              <FormSubmitControls
                onFinish={() => form.submit()}
                onCancel={onCancel}
                loading={loading}
              />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError
              name="general"
            >
              <Card
                type="inner"
                size="small"
                className="inner-block-card"
                title="1. Identificación De La Percepción"
                extra="Concepto Y Aplicación Base"
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="percepcion"
                      label="Concepto de Percepción"
                      rules={rules.contratacion}
                      hasFeedback
                    >
                      <Select
                        keyLabelRender
                        dataSource={percepcionesFiltered}
                        onChange={onChangeAplicacion}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="aplicacion_contribucion"
                      label="Aplicación para ISN"
                      rules={rules.required}
                      hasFeedback
                    >
                      <Select
                        dataSource={aplicaciones}
                        onChange={onChangeAplicacion}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="fundamento"
                      label="Fundamento"
                      hasFeedback
                    >
                      <Input
                        allowClear
                        disabled={aplicaFundamento}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
              <Card
                type="inner"
                size="small"
                className="inner-block-card"
                title="2. Configuración De Exención"
                extra="UMA, Importe Fijo O Porcentaje"
              >
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 15 }}
                  message="Solo uno de los tres métodos de exención debe estar activo a la vez: UMAs exento, Importe exento fijo o Porcentaje de exención. Activar más de uno puede generar cálculos incorrectos."
                />
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="aplica_no_umas_excento"
                      label="Aplica Número de UMAs Exento"
                      rules={!aplicacionISN ? rules.required : false}
                      initialValue={false}
                      hasFeedback
                    >
                      <Select
                        trueFalse
                        disabled={aplicacionISN}
                        onChange={(value) => {
                          setAplicaUMAExcento(value);
                          form.resetFields(['no_umas_excento']);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <NumericInput
                      name="no_umas_excento"
                      label="Número de UMAs Exento"
                      rules={aplicaUMAExcento ? rules.valor : null}
                      disabled={!aplicaUMAExcento}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="aplica_importe_excento"
                      rules={!aplicacionISN ? rules.required : false}
                      label="Aplica Importe Exento"
                      initialValue={false}
                      hasFeedback
                    >
                      <Select
                        trueFalse
                        disabled={aplicacionISN}
                        onChange={(value) => {
                          setAplicaImporte(value);
                          form.resetFields(['importe_excento']);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <CurrencyInput
                      name="importe_excento"
                      label="Importe Exento"
                      rules={aplicaImporte ? rules.valor : null}
                      disabled={!aplicaImporte}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="aplica_porcentaje_excento"
                      rules={!aplicacionISN ? rules.required : false}
                      label="Aplica Porcentaje de Exención"
                      initialValue={false}
                      hasFeedback
                    >
                      <Select
                        trueFalse
                        disabled={aplicacionISN}
                        onChange={(value) => {
                          setAplicaPorcentaje(value);
                          form.resetFields(['porcentaje_excento']);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={12}>
                    <PercentageInput
                      name="porcentaje_excento"
                      label="Porcentaje de Exención"
                      rules={aplicaPorcentaje ? rules.porcentaje : null}
                      disabled={!aplicaPorcentaje}
                    />
                  </Col>
                </Row>
              </Card>
              <Card
                type="inner"
                size="small"
                className="inner-block-card"
                title="3. Límite De La Exención"
                extra="Tope Máximo Aplicable"
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="aplica_limite_de_excencion"
                      rules={!aplicacionISN ? rules.required : false}
                      label="Tiene Límite la Exención"
                      initialValue={false}
                      hasFeedback
                    >
                      <Select
                        trueFalse
                        disabled={aplicacionISN}
                        onChange={(value) => {
                          setAplicaLimite(value);
                          form.resetFields(['no_umas_limite_de_excencion']);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <NumericInput
                      name="no_umas_limite_de_excencion"
                      label="Número de UMAs Límite"
                      rules={aplicaLimite ? rules.dias : null}
                      disabled={!aplicaLimite}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="periodicidad_de_pago"
                      label="Periodicidad de Aplicación de la Exención"
                      rules={!aplicacionISN ? rules.required : false}
                      hasFeedback
                    >
                      <Select
                        disabled={aplicacionISN}
                        dataSource={periodicidades}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
              <Card
                type="inner"
                size="small"
                className="inner-block-card"
                title="4. Control Y Vigencia"
                extra="Estatus Y Fecha De Vencimiento"
              >
                <Row gutter={10}>
                  {selectedRowKeys.length
                    ? (
                      <Col xs={24} sm={24} md={12}>
                        <Form.Item
                          name="estados_globales"
                          label="Estatus"
                          rules={rules.estatus}
                          hasFeedback
                        >
                          <Select
                            dataSource={estados}
                          />
                        </Form.Item>
                      </Col>
                    )
                    : (
                      <Col xs={24} sm={24} md={12}>
                        <Form.Item
                          name="estados_globales"
                          label="Estatus"
                          rules={rules.estatus}
                          hasFeedback
                          initialValue={1}
                        >
                          <Select
                            dataSource={estados}
                            disabled
                          />
                        </Form.Item>
                      </Col>
                    )}
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="vigente_al"
                      label="Vigente Al"
                      hasFeedback
                    >
                      <DatePicker
                        allowClear
                        format="DD/MM/YYYY"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={24}>
                    <Form.Item
                      name="observaciones"
                      label="Observaciones"
                      hasFeedback
                    >
                      <TextArea
                        allowClear
                        rows={3}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Percepción Configuración ISN ${contentModalDelete(form.getFieldValue('percepcion'))}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default PercepcionConfiguracionISN;
